import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, Users, Circle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

export default function Chat({ user }) {
  const [messages, setMessages] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [connected, setConnected] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const shouldReconnectRef = useRef(true);

  useEffect(() => {
    shouldReconnectRef.current = true;
    fetchMessages();
    connectWebSocket();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/chat/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const since = localStorage.getItem('chatSince');
        // Admin sees full history; others only messages since this login.
        setMessages(since && user?.role !== 'admin' ? data.filter(m => m.timestamp >= since) : data);
      }
    } catch {
      toast.error('Mesajlar yüklenirken hata oluştu');
    }
  };

  const connectWebSocket = () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const websocket = new WebSocket(`${WS_URL}/ws/chat?token=${token}`);
    wsRef.current = websocket;

    websocket.onopen = () => {
      setConnected(true);
      toast.success('Sohbete bağlandınız');
    };

    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'presence') {
        setOnlineUsers(data.users || []);
      } else if (data.type === 'message' || data.message) {
        // Backward compat: if no type, treat as message
        setMessages(prev => [...prev, data]);
      }
    };

    websocket.onerror = () => {
      // Silently handle; onclose will run reconnect
    };

    websocket.onclose = () => {
      setConnected(false);
      setOnlineUsers([]);
      if (shouldReconnectRef.current) {
        reconnectTimerRef.current = setTimeout(connectWebSocket, 3000);
      }
    };
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    const ws = wsRef.current;
    if (!newMessage.trim() || !ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(newMessage);
    setNewMessage('');
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-8 sm:py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-7 sm:mb-9 fade-in">
          <h1 className="text-3xl sm:text-5xl font-bold text-amber-900 mb-3" style={{ fontFamily: 'Merriweather, serif' }}>
            Sohbet Odası
          </h1>
          <p className="text-base sm:text-lg text-amber-700">
            Üyelerimizle canlı sohbet edin
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4 sm:gap-6">
          {/* Chat Card */}
          <Card className="border-2 border-amber-200 bg-white shadow-xl overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-amber-100 to-orange-100 py-3 sm:py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-amber-600 to-orange-600 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <CardTitle className="text-amber-900 text-base sm:text-lg">Topluluk Sohbeti</CardTitle>
                </div>
                <div className="flex items-center gap-2" data-testid="chat-status">
                  <div className={`w-2.5 h-2.5 rounded-full ${connected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                  <span className="text-xs sm:text-sm text-amber-700 font-medium">
                    {connected ? 'Çevrimiçi' : 'Çevrimdışı'}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="h-[440px] sm:h-[500px] overflow-y-auto p-4 sm:p-6 space-y-3 bg-amber-50/50" data-testid="chat-messages-container">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-amber-600 text-sm">Henüz mesaj yok. İlk mesajı siz gönderin!</p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isOwn = msg.user_id === user.id;
                    return (
                      <div
                        key={msg.id}
                        className={`chat-message flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                        data-testid={`chat-message-${msg.id}`}
                      >
                        <div
                          className={`max-w-[78%] sm:max-w-[70%] rounded-2xl px-4 py-2.5 sm:py-3 shadow-sm ${
                            isOwn
                              ? 'bg-gradient-to-br from-amber-600 to-orange-600 text-white'
                              : 'bg-white border border-amber-200 text-amber-900'
                          }`}
                        >
                          {!isOwn && (
                            <p className="text-xs sm:text-sm font-semibold mb-1 text-orange-700">
                              {msg.nickname}
                            </p>
                          )}
                          <p className="break-words text-sm sm:text-[15px]">{msg.message}</p>
                          <p className={`text-[10px] sm:text-xs mt-1 ${isOwn ? 'text-amber-100/80' : 'text-amber-500'}`}>
                            {formatTime(msg.timestamp)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="p-3 sm:p-4 bg-white border-t-2 border-amber-100">
                <form onSubmit={handleSendMessage} className="flex gap-2 sm:gap-3">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Mesajınızı yazın..."
                    className="flex-1 border-amber-300 focus:border-amber-500"
                    disabled={!connected}
                    data-testid="chat-input"
                  />
                  <Button
                    type="submit"
                    className="bg-gradient-to-r from-amber-600 to-orange-600 text-white hover:from-amber-700 hover:to-orange-700"
                    disabled={!connected || !newMessage.trim()}
                    data-testid="chat-send-btn"
                  >
                    <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                </form>
              </div>
            </CardContent>
          </Card>

          {/* Online Users sidebar */}
          <Card className="border-2 border-amber-200 bg-white shadow-xl h-fit lg:sticky lg:top-20" data-testid="online-users-card">
            <CardHeader className="bg-gradient-to-r from-amber-100 to-orange-100 py-3 sm:py-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-amber-900 text-base flex items-center gap-2">
                  <Circle className="w-2.5 h-2.5 fill-green-500 text-green-500" />
                  Çevrimiçi
                </CardTitle>
                <span className="text-xs font-semibold text-amber-700 bg-white/70 px-2 py-0.5 rounded-full">
                  {onlineUsers.length}
                </span>
              </div>
            </CardHeader>
            <CardContent className="p-3 sm:p-4">
              {onlineUsers.length === 0 ? (
                <p className="text-amber-600 text-sm text-center py-4">Şu an kimse çevrimiçi değil</p>
              ) : (
                <ul className="space-y-2 max-h-[440px] overflow-y-auto" data-testid="online-users-list">
                  {onlineUsers.map((u) => (
                    <li key={u.user_id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-amber-50 transition-colors" data-testid={`online-user-${u.user_id}`}>
                      <div className="relative">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold text-sm">
                          {u.nickname?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-500 border-2 border-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-amber-900 truncate">
                          {u.nickname}
                          {u.user_id === user.id && <span className="text-amber-500 text-xs ml-1">(siz)</span>}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
